import dayjs from 'dayjs';
import { DEFAULT_START_TIME } from './constants';

export function capitalizeString(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const formatNumber = (num = 0): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

export const formatFare = (num = 0): string => {
  return `đ${new Intl.NumberFormat('en-US').format(num)}`;
};

export const formatCurrency = (num = 0): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
  }).format(num);
};

function repeat(s: string, times: number): string {
  return new Array(times + 1).join(s);
}

export const pad = (num: number, maxLength: number): string => {
  return repeat('0', maxLength - num.toString().length) + num;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const loadJS = (src: string, attr?: { [key: string]: any }) => {
  return new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.setAttribute('src', src);
    tag.setAttribute('type', 'text/javascript');
    if (attr) {
      for (const key in attr) {
        tag.setAttribute(key, attr[key]);
      }
    }
    tag.onload = resolve;

    tag.onerror = reject;

    document.head.appendChild(tag);
  });
};

export const cleanAccents = (str: string): string => {
  str = str.replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a');
  str = str.replace(/[èéẹẻẽêềếệểễ]/g, 'e');
  str = str.replace(/[ìíịỉĩ]/g, 'i');
  str = str.replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o');
  str = str.replace(/[ùúụủũưừứựửữ]/g, 'u');
  str = str.replace(/[ỳýỵỷỹ]/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/[ÀÁẠẢÃÂẦẤẬẨẪĂẰẮẶẲẴ]/g, 'A');
  str = str.replace(/[ÈÉẸẺẼÊỀẾỆỂỄ]/g, 'E');
  str = str.replace(/[ÌÍỊỈĨ]/g, 'I');
  str = str.replace(/[ÒÓỌỎÕÔỒỐỘỔỖƠỜỚỢỞỠ]/g, 'O');
  str = str.replace(/[ÙÚỤỦŨƯỪỨỰỬỮ]/g, 'U');
  str = str.replace(/[ỲÝỴỶỸ]/g, 'Y');
  str = str.replace(/Đ/g, 'D');
  // Combining Diacritical Marks
  str = str.replace(/[\u0300\u0301\u0303\u0309\u0323]/g, ''); // huyền, sắc, hỏi, ngã, nặng
  // eslint-disable-next-line no-misleading-character-class
  str = str.replace(/[\u02C6\u0306\u031B]/g, ''); // mũ â (ê), mũ ă, mũ ơ (ư)

  return str;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const uniqueArray = (arr: Array<any> = []) => {
  const uniqueIds = new Set();

  const unique = arr.filter((element) => {
    const isDuplicate = uniqueIds.has(element._id);
    uniqueIds.add(element._id);
    if (!isDuplicate) {
      return true;
    }
    return false;
  });

  return unique;
};

export const generateLetterAvatar = (name = '') =>
  `${name.split(' ')[0] ? name.split(' ')[0][0] : ''}${
    name.split(' ')[1] ? name.split(' ')[1][0] : ''
  }`;

export const escapeRegExp = (value: string): string => {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export const getDiffBetweenTwoArray = (arr1: string[], arr2: string[]) => {
  function getDifference(a: string[], b: string[]) {
    return a.filter((element) => {
      return !b.includes(element);
    });
  }

  return [...getDifference(arr1, arr2), ...getDifference(arr2, arr1)];
};

export const randomId = (): string => {
  const uint32 = window.crypto.getRandomValues(new Uint32Array(1))[0];
  return uint32.toString(16);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const applyToArray = (func: any, array: number[]): number =>
  func.apply(Math, array);

export const roundedTime = (dateInput: dayjs.Dayjs): dayjs.Dayjs => {
  let newDate = dayjs();

  const h = dateInput.hour();
  const m = dateInput.minute();

  if (m > 30) {
    newDate = dateInput
      .set('hour', h + 1)
      .set('minute', 0)
      .set('second', 0);
  } else {
    newDate = dateInput.set('minute', 30).set('second', 0);
  }

  return newDate;
};

export const getValidStartTime = (
  inputDate: dayjs.Dayjs,
  timeConfig = 0,
  DEFAULT_TIME_RANGE: number[]
): number => {
  const now = dayjs();
  const isNow = inputDate.isSame(now, 'date');

  const hh = inputDate.hour();
  const mm = inputDate.minute();

  if (isNow) {
    const timeWithConfig = roundedTime(now.add(timeConfig, 'second'));
    const dateWithConfig = timeWithConfig.date();
    const hourWithConfig = timeWithConfig.hour();
    const minuteWithConfig = timeWithConfig.minute();

    const nextDateUnix = now
      .startOf('day')
      .set('date', now.date() + 1)
      .set('hour', DEFAULT_START_TIME.hour)
      .set('minute', DEFAULT_START_TIME.minute)
      .unix();

    if (dateWithConfig > now.date()) return nextDateUnix;

    if (hourWithConfig > DEFAULT_TIME_RANGE[1]) return nextDateUnix;

    if (hourWithConfig === DEFAULT_TIME_RANGE[1]) {
      if (minuteWithConfig === 0)
        return now.startOf('day').set('hour', DEFAULT_TIME_RANGE[1]).unix();

      return nextDateUnix;
    }

    if (hourWithConfig < DEFAULT_TIME_RANGE[0])
      return now

        .set('hour', DEFAULT_START_TIME.hour)
        .set('minute', DEFAULT_START_TIME.minute)
        .set('second', 0)
        .unix();

    return timeWithConfig.unix();
  } else {
    if (
      hh < DEFAULT_TIME_RANGE[0] ||
      hh > DEFAULT_TIME_RANGE[1] ||
      (hh === DEFAULT_TIME_RANGE[1] && mm > 0)
    ) {
      return inputDate
        .startOf('day')
        .set('hour', DEFAULT_START_TIME.hour)
        .set('minute', DEFAULT_START_TIME.minute)
        .unix();
    }

    return inputDate.unix();
  }
};

export const getRemainCountDownTime = (time: number) => {
  const defaultCountdownTime = 0;
  const targetTime = new Date(time * 1000 + 15 * 60 * 1000).getTime();
  const now = new Date().getTime();
  const runway = Math.ceil(targetTime - now);
  if (runway > 0) return runway;
  return defaultCountdownTime;
};

export function detectPlatform(userAgent: string): string {
  const isWindows = /Windows/i.test(userAgent);
  const isMacOS = /Macintosh/i.test(userAgent);
  const isLinux = /Linux/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

  if (isWindows) {
    return 'Windows';
  } else if (isMacOS) {
    return 'MacOS';
  } else if (isLinux) {
    return 'Linux';
  } else if (isAndroid) {
    return 'Android';
  } else if (isIOS) {
    return 'iOS';
  } else {
    return 'Unknown';
  }
}
